import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { UserProvider, useUser } from "./contexts/UserContext";
import { ChatProvider } from "./contexts/ChatContext";
import { CodesProvider } from "./contexts/CodesContext";
import Header from "./components/Header";
import Login from "./pages/Login";
import CarsList from "./pages/CarsList";
import CodesList from "./pages/CodesList";
import UserList from "./pages/UserList";
import TestDriveList from "./pages/TestDriveList";
import TestDriveStart from "./pages/TestDriveStart";
import TestDriveAdminHelp from "./pages/TestDriveAdminHelp";
import CarInspectionsTable from "./pages/CarInspectionTable";
import AccidentReportTable from "./pages/AccidentReportTable";
import BigCarSubmissionsTable from "./pages/BigCarSubmissionsTable";
import MaagarSubmissionsTable from "./pages/MaagarSubmissionsTable";
import ElectricCarsSubmissionsTable from "./pages/ElectricCarsSubmissionsTable";
import DriverStatementSubmissionsTable from "./pages/DriverStatementSubmissionsTable";
import NewDriverStatementSubmissionsTable from "./pages/NewDriverStatementSubmissionsTable";
import FamilyFormSubmissionsTable from "./pages/FamilyFormSubmissionsTable";
import LoginLogs from "./pages/LoginLogs";
import SocketComponent from "./utils/openWebSocket";
import Chat from "./pages/Chat";
import "./App.css";
import { USER_TYPES, UserTypeValue } from "./types/userType";
import { getDefaultRoute, toRoleArray } from "./utils/roles";
import { BarLoader } from "react-spinners";
import { DesignSystemVariables } from "./design-system";

const Stats = React.lazy(() => import("./pages/Stats"));

// Unlock audio playback on first user interaction
function useUnlockAudio() {
  React.useEffect(() => {
    const unlock = () => {
      const audio = new window.Audio("/notification.mp3");
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
        })
        .catch(() => {});
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);
}


const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowed?: UserTypeValue[];
}> = ({ children, allowed }) => {
  const { isAuthenticated, userType } = useUser();
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const roles = toRoleArray(userType);
  if (allowed && !roles.some((role) => allowed.includes(role))) {
    return <Navigate to={getDefaultRoute(roles)} replace />;
  }

  return <>{children}</>;
};

const DefaultRedirect: React.FC = () => {
  const { userType } = useUser();
  const roles = toRoleArray(userType);
  return <Navigate to={getDefaultRoute(roles)} replace />;
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated } = useUser();
  const hideHeaderFooter = location.pathname === "/login" || !isAuthenticated;
  const isChatPage = location.pathname === "/chat";

  const content = (
    <>
      {!hideHeaderFooter && <Header />}
      <main style={{ minHeight: "80vh" }}>{children}</main>
      {/* {!hideHeaderFooter && <Footer />} */}
    </>
  );

  if (isChatPage && !hideHeaderFooter) {
    return <ChatProvider>{content}</ChatProvider>;
  }

  return content;
};

const App: React.FC = () => {
  useUnlockAudio();
  return (
    <DesignSystemVariables>
      <UserProvider>
        <CodesProvider>
          <Router>
          <SocketComponent />
          <Layout>
            <Routes>
              <Route
                path="/chat"
                element={
                  <ProtectedRoute
                    allowed={[
                      USER_TYPES.ADMIN,
                      USER_TYPES.SUPER_ADMIN,
                      USER_TYPES.RENTAL,
                    ]}
                  >
                    <Chat />
                  </ProtectedRoute>
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/test-drive/start" element={<TestDriveStart />} />
              <Route path="/test-drive/admin-help" element={<TestDriveAdminHelp />} />
              <Route
                path="/cars"
                element={
                  <ProtectedRoute
                    allowed={[
                      USER_TYPES.ADMIN,
                      USER_TYPES.SUPER_ADMIN,
                      USER_TYPES.RENTAL,
                    ]}
                  >
                    <CarsList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/codes"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <CodesList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <UserList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/login-logs"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <LoginLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/test-drive"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <TestDriveList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/car-inspection-table"
                element={
                  <ProtectedRoute
                    allowed={[
                      USER_TYPES.ADMIN,
                      USER_TYPES.SUPER_ADMIN,
                      USER_TYPES.INSPECTION,
                    ]}
                  >
                    <CarInspectionsTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accident-report-table"
                element={
                  <ProtectedRoute
                    allowed={[
                      USER_TYPES.ADMIN,
                      USER_TYPES.SUPER_ADMIN,
                      USER_TYPES.OPERATE,
                      USER_TYPES.FREESBE,
                    ]}
                  >
                    <AccidentReportTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/big-car-submissions-table"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <BigCarSubmissionsTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/maagar-submissions-table"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <MaagarSubmissionsTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/electric-cars-submissions-table"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <ElectricCarsSubmissionsTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/driver-statement-submissions-table"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <DriverStatementSubmissionsTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/new-driver-statement-submissions-table"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <NewDriverStatementSubmissionsTable />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/family-form-submissions-table"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <FamilyFormSubmissionsTable />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/app-stats"
                element={
                  <ProtectedRoute
                    allowed={[USER_TYPES.ADMIN, USER_TYPES.SUPER_ADMIN]}
                  >
                    <Suspense fallback={<></>}>
                      <Stats />
                    </Suspense>
                  </ProtectedRoute>
                }
              />

              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <DefaultRedirect />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Layout>
          </Router>
        </CodesProvider>
      </UserProvider>
    </DesignSystemVariables>
  );
};

export default App;
